"use client";

/**
 * `RatePicker` — stateless presentation primitive for the live-rate buyer flow.
 *
 * Layer: Primitive (`react-primitive-design §1`). Props in → DOM out. No state,
 * no data fetch, no contexts, no business decisions (no sort / filter / dedup —
 * parent passes `rates` in render order).
 *
 * Display state = discriminated prop, NOT internal booleans. Switch on `display`
 * ends with `default: unreachable(x: never)` so a new variant fails compile at
 * every render site (`type-driven-design §2, §3`).
 *
 * A11y: WAI-ARIA radiogroup pattern via `@radix-ui/react-radio-group` — Root
 * exposes `role="radiogroup"`, items expose `role="radio"` + `aria-checked` +
 * roving tabindex, ArrowUp/ArrowDown/Home/End move focus AND selection, Space
 * confirms the focused option. We trade hand-rolled keyboard handling for the
 * Radix implementation that already covers the WAI-ARIA spec edge cases
 * (`react-frontend-foundations §A1`).
 *
 * Currency: `Intl.NumberFormat(undefined, { style:'currency', currency })`.
 * NEVER bare `$` — marketplace is multi-currency (memory
 * `feedback_currency_check_before_dollar_sign`). Locale is undefined so the
 * environment decides — tests stay locale-tolerant.
 *
 * @see .tondocs/shippo/PLAN.md §D WS4 — workstream context
 * @see .tondocs/skills/react-primitive-design.md §1, §2 — primitive layer + forwardRef
 * @see .tondocs/skills/type-driven-design.md §2, §3 — display ADT + never-probe
 * @see .tondocs/skills/react-frontend-foundations.md §A1 — radiogroup + keyboard
 * @see lib/shipping/types.ts — `Rate` ADT (the shape this primitive renders)
 * @see hooks/use-shipping-rates.ts — `ShippingRateSelection` consumer pairing
 */

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import type { Rate } from "@/lib/shipping/types";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Display-only FSM. Parent's behaviour layer dictates which arm renders. */
export type RatePickerDisplay = "idle" | "loading" | "loaded" | "error";

export interface RatePickerProps {
  /** Rates to render. Pass `[]` and `display='loaded'` for the empty state. */
  rates: ReadonlyArray<Rate>;
  /** Currently selected `Rate.id` (opaque kraahl id), or `null` if no selection. */
  selectedId: string | null;
  /** Called with the picked rate's id when buyer selects. */
  onSelect: (rateId: string) => void;
  /**
   * Display-only state (parent's FSM dictates which). Default: `'loaded'`.
   *  - `loaded`: render rates (or empty state if `rates.length === 0`).
   *  - `loading`: skeleton row(s); rates ignored.
   *  - `error`: error affordance with the message; rates ignored.
   *  - `idle`: muted placeholder (e.g. "Enter address to see rates"); rates ignored.
   */
  display?: RatePickerDisplay;
  /** Error message to show when `display === 'error'`. Plain text, no HTML. */
  errorMessage?: string;
  /** When true, the whole picker is non-interactive (e.g. during a submit). */
  disabled?: boolean;
  /** Optional className passthrough. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function unreachable(x: never): never {
  throw new Error(`RatePicker: unreachable display variant: ${JSON.stringify(x)}`);
}

const DEFAULT_ARIA_LABEL = "Shipping rate options";
/** Skeleton row count — kept hardcoded at the primitive layer; if parents need to override it, lift to a prop in WS4.3b. */
const SKELETON_ROW_COUNT = 3;

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(amount);
}

function formatDuration(days: number | null): string | null {
  if (days === null) return null;
  if (days <= 0) return null;
  return days === 1 ? "1 day" : `${days} days`;
}

// ---------------------------------------------------------------------------
// Sub-renders (kept inline — primitive-local, no behaviour, no exports)
// ---------------------------------------------------------------------------

function IdleState({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      className={cn("rounded-md bg-muted/30 p-4 text-base text-muted-foreground", className)}
      data-testid="rate-picker-idle"
      role="status"
    >
      Enter a shipping address to see rates.
    </div>
  );
}

function LoadingState({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      className={cn("space-y-2", className)}
      data-testid="rate-picker-loading"
      aria-busy="true"
      aria-live="polite"
      role="status"
    >
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <div
          key={i}
          className="h-16 w-full animate-pulse rounded-md border border-input bg-muted"
        />
      ))}
    </div>
  );
}

function ErrorState({
  message,
  className,
}: {
  message: string;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "rounded-md border border-destructive/40 bg-destructive/10 p-4 text-base text-destructive",
        className,
      )}
      role="alert"
    >
      {message}
    </div>
  );
}

function EmptyState({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      className={cn("rounded-md bg-muted/30 p-4 text-base text-muted-foreground", className)}
      data-testid="rate-picker-empty"
      role="status"
    >
      No rates available for this address.
    </div>
  );
}

function RateRow({
  rate,
  isSelected,
  disabled,
}: {
  rate: Rate;
  isSelected: boolean;
  disabled: boolean;
}): React.ReactElement {
  const price = formatAmount(rate.amount, rate.currency);
  const duration = formatDuration(rate.durationDays);
  return (
    <RadioGroupPrimitive.Item
      value={rate.id}
      disabled={disabled}
      className={cn(
        // `min-w-0` on the button itself is load-bearing: without it the
        // button's intrinsic min-content (carrier+service text in `truncate`
        // + nowrap price) propagates up through Root → section → grid item
        // and pushes the whole checkout sheet wider than the viewport on
        // mobile (operator-reported overflow, 2026-06-25).
        "group flex w-full min-w-0 items-center justify-between gap-4 rounded-md border bg-card p-4 text-left text-base text-card-foreground transition-colors",
        "border-input hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "data-[state=checked]:border-primary data-[state=checked]:bg-primary/5",
        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium">
          {rate.carrier} · {rate.service}
        </span>
        {duration !== null && (
          <span className="truncate text-sm text-muted-foreground">
            Est. {duration}
          </span>
        )}
      </span>
      <span
        className={cn(
          "shrink-0 text-base font-semibold tabular-nums",
          isSelected ? "text-primary" : "text-foreground",
        )}
      >
        {price}
      </span>
    </RadioGroupPrimitive.Item>
  );
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export const RatePicker = React.forwardRef<HTMLElement, RatePickerProps>(
  function RatePicker(
    {
      rates,
      selectedId,
      onSelect,
      display = "loaded",
      errorMessage,
      disabled = false,
      className,
    },
    ref,
  ) {
    switch (display) {
      case "idle":
        return (
          <div ref={ref as React.Ref<HTMLDivElement>} className={className}>
            <IdleState />
          </div>
        );
      case "loading":
        return (
          <div ref={ref as React.Ref<HTMLDivElement>} className={className}>
            <LoadingState />
          </div>
        );
      case "error": {
        // Narrow before forwarding — empty/undefined → fallback string,
        // avoids passing `undefined` to a child that expects `string`.
        //
        // T13 (2026-07-23): the container (`LiveRateSubflow.deriveErrorCopy`)
        // now translates EVERY `ShippingRatesErrorCode` and never forwards an
        // empty/undefined `errorMessage` for a real error — this branch is a
        // defensive last resort, not a code path any current caller reaches.
        // Deliberately kept as a plain English string, NOT `useTranslations`:
        // this is a Primitive layer (react-primitive-design §1), so it must
        // stay prop-driven and framework/locale-agnostic — a primitive that
        // needs a next-intl provider to render can't be used in a dev
        // showcase (`.tondocs/ui/DEV_PAGES.md`) or any future non-i18n
        // consumer. The translated fallback lives one layer up, in
        // `errorGeneric` (`messages/*.json` → `shippoCheckout`), which is
        // exactly the string below in English.
        const message =
          typeof errorMessage === "string" && errorMessage.length > 0
            ? errorMessage
            : "Could not load shipping rates.";
        return (
          <div ref={ref as React.Ref<HTMLDivElement>} className={className}>
            <ErrorState message={message} />
          </div>
        );
      }
      case "loaded": {
        if (rates.length === 0) {
          return (
            <div ref={ref as React.Ref<HTMLDivElement>} className={className}>
              <EmptyState />
            </div>
          );
        }
        return (
          <RadioGroupPrimitive.Root
            ref={ref as React.Ref<HTMLDivElement>}
            className={cn("flex w-full min-w-0 flex-col gap-2", className)}
            value={selectedId ?? undefined}
            onValueChange={onSelect}
            disabled={disabled}
            aria-label={DEFAULT_ARIA_LABEL}
          >
            {rates.map((rate) => (
              <RateRow
                key={rate.id}
                rate={rate}
                isSelected={rate.id === selectedId}
                disabled={disabled}
              />
            ))}
          </RadioGroupPrimitive.Root>
        );
      }
      default:
        return unreachable(display);
    }
  },
);

RatePicker.displayName = "RatePicker";
